import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import T from '@wojtekmaj/react-t';
import { CheckIcon, LockIcon } from 'lucide-react';

import {
  Amount,
  ButtonWrapper,
  CheckIconWrapper,
  ContentWrapper,
  Description,
  Footer,
  Hint,
  Icon,
  Name,
  Price,
  ScrollContainer,
  Wrapper,
} from './index.styles';

import I18nText from '../../../../../../i18n_text';
import HeaderWithButton from '../../../../header_with_button';
import ActivateButton from './activate_button';

import { formatNumber } from '../../../../../../../utils/helpers';

import {
  transactionsQuery,
  vendorCardRewardQuery,
  vendorCardSubscriptionState,
} from '../../../../../../../store';

import type { Reward, Transaction } from '@rewardopl/types';

export default function RewardPage() {
  const navigate = useNavigate();
  const { reward_id: rewardId } = useParams<{ reward_id?: Reward['_id'] }>();

  if (!rewardId) {
    throw new Error('rewardId is undefined');
  }

  const cardSubscription = useAtomValue(vendorCardSubscriptionState);

  const { _id: cardSubscriptionId, balance } = cardSubscription;

  const setTransactions = useSetAtom(transactionsQuery(cardSubscriptionId));
  const reward = useAtomValue(vendorCardRewardQuery(rewardId));

  if (!reward) {
    return <Navigate to=".." />;
  }

  const {
    description,
    description_i18n: descriptionI18n,
    name,
    name_i18n: nameI18n,
    price,
  } = reward;

  async function onSuccess(nextTransaction: Transaction) {
    setTimeout(() => {
      navigate(`../../reward_received/${nextTransaction._id}`);
    }, 0);
  }

  const isAvailable = balance >= price;

  return (
    <Wrapper>
      <ScrollContainer>
        <HeaderWithButton>Reward</HeaderWithButton>
        <ContentWrapper>
          <Icon>
            {isAvailable ? (
              <CheckIconWrapper>
                <CheckIcon size={12} strokeWidth={4} />
              </CheckIconWrapper>
            ) : (
              <LockIcon color="var(--text-light)" size={16} strokeWidth={3} />
            )}
          </Icon>
          <Name>
            <I18nText fallbackValue={name} value={nameI18n} />
          </Name>
          <Price>
            <Amount>{formatNumber(price)}</Amount>
            <T>{`sweet${price === 1 ? '' : 's'}`}</T>
          </Price>
          {description ? (
            <Description>
              <I18nText fallbackValue={description ?? ''} value={descriptionI18n} />
            </Description>
          ) : null}
          <Hint>
            {isAvailable ? (
              <T price={price}>
                {
                  'After clicking “Activate” you’ll receive reward, and {price} sweets will be taken from your account.'
                }
              </T>
            ) : (
              <T>You don’t have enough sweets to get this reward.</T>
            )}
          </Hint>
        </ContentWrapper>
      </ScrollContainer>
      <Footer>
        <ButtonWrapper>
          <ActivateButton
            disabled={!isAvailable}
            onSuccess={onSuccess}
            reward={reward}
            setTransactions={setTransactions}
          />
        </ButtonWrapper>
      </Footer>
    </Wrapper>
  );
}
